import React from 'react';
import type {
  Candidate,
  CandidateReputationDetails,
  User,
} from '@axiom/validation';
import {
  Badge,
  DataGridContextMenuItem,
  DataGridHeader,
  DateUtil,
  Flashy,
  IconButton,
  Layout,
} from '@axiom/ui';
import type { GetContextMenuItemsParams } from '@axiom/ui';
import { isEnvoyAdmin, isEnvoyUser } from '@axiom/utils';

import { CandidateReputationDetailsApi } from '../../api/candidate-reputation-details-api';

const EMPTY_VALUE = 'N/A';

enum ReputationDetailsStatus {
  AUTO_SENDING = 'AutoSending',
  COACHING = 'Coaching',
  DISPUTED = 'Disputed',
  DISMISSED = 'Dismissed',
  LOW_SCORE = 'LowScore',
  NEW = 'New',
  SHARED = 'Shared',
  SHARED_LIVE = 'SharedLive',
  HISTORICAL = 'Historical',
  PROCESSED = 'Processed',
}

type ContextMenuItem = {
  name: string;
  menuItem: typeof DataGridContextMenuItem;
  menuItemParams: { testId: string };
  action: () => void;
};

const createMenuItem = ({
  name,
  testId,
  action,
}: {
  name: string;
  testId: string;
  action: () => void;
}) => ({
  name,
  menuItem: DataGridContextMenuItem,
  menuItemParams: { testId },
  action,
});

type ScoreField =
  | 'legalExpertiseScore'
  | 'businessJudgementScore'
  | 'communicationScore'
  | 'responsivenessScore';

const scoreOutOf5CellRender = ({ field }: { field: ScoreField }) =>
  function ({ data }: { data: CandidateReputationDetails }) {
    const value = data[field];
    return (
      <Flashy color="dataVizGreen">
        {value ? `${value.toFixed(1)}/5` : EMPTY_VALUE}
      </Flashy>
    );
  };

const scoreColumns = [
  {
    key: 'legalExpertiseScore' as const,
    width: 152,
    displayName: 'Legal Acumen',
  },
  {
    key: 'businessJudgementScore' as const,
    width: 152,
    displayName: 'Bus Judgement',
  },
  {
    key: 'communicationScore' as const,
    width: 144,
    displayName: 'Communication',
  },
  {
    key: 'responsivenessScore' as const,
    width: 144,
    displayName: 'Responsiveness',
  },
];

const statusBadgeConfig: Record<
  string,
  { background: 'yellow' | 'red' | 'gray'; label: string }
> = {
  [ReputationDetailsStatus.NEW]: { background: 'yellow', label: 'New' },
  [ReputationDetailsStatus.LOW_SCORE]: {
    background: 'red',
    label: 'Low Score',
  },
  [ReputationDetailsStatus.COACHING]: { background: 'red', label: 'Coaching' },
  [ReputationDetailsStatus.AUTO_SENDING]: {
    background: 'red',
    label: 'Auto-sending',
  },
  [ReputationDetailsStatus.DISPUTED]: { background: 'gray', label: 'Disputed' },
  [ReputationDetailsStatus.DISMISSED]: {
    background: 'gray',
    label: 'Dismissed',
  },
  [ReputationDetailsStatus.SHARED]: {
    background: 'gray',
    label: 'Shared by Email',
  },
  [ReputationDetailsStatus.SHARED_LIVE]: {
    background: 'gray',
    label: 'Shared live',
  },
  [ReputationDetailsStatus.HISTORICAL]: {
    background: 'gray',
    label: 'Historical',
  },
  [ReputationDetailsStatus.PROCESSED]: {
    background: 'gray',
    label: 'Processed',
  },
};

export const ReputationDetailsUtil = {
  formatScores: (score: number | null): string => {
    return score == null ? EMPTY_VALUE : score.toFixed(1);
  },
  handleBadge: ({ data }: { data: CandidateReputationDetails }) => {
    const config = data.status ? statusBadgeConfig[data.status] : undefined;
    return config ? (
      <Badge name="REPUTATION_STATUS_BADGE" background={config.background}>
        {config.label}
      </Badge>
    ) : null;
  },
  buildGridColumns: ({
    candidateId,
    setHistoryData,
  }: {
    candidateId?: Candidate['id'];
    setHistoryData: (data: CandidateReputationDetails) => void;
  }) => {
    const columns: React.ReactElement[] = [
      <DataGridHeader<CandidateReputationDetails>
        key="status"
        name="status"
        width={184}
        pinned="left"
        displayName="Status"
        sortingOrder={['desc', 'asc', null]}
        cellRender={ReputationDetailsUtil.handleBadge}
      />,
      <DataGridHeader<CandidateReputationDetails>
        key="responseDate"
        name="responseDate"
        width={128}
        pinned="left"
        displayName="Date"
        sortingOrder={['desc', 'asc']}
        initialSort="desc"
        cellRender={({ data }) =>
          DateUtil.displayDateFullYearShortMonth(data.responseDate)
        }
      />,
      <DataGridHeader<CandidateReputationDetails>
        key="accountName"
        name="accountName"
        width={128}
        pinned="left"
        displayName="Account"
        sortingOrder={['asc', 'desc', null]}
        cellRender={({ data }) => (
          <div>{data.opportunity.account?.name || EMPTY_VALUE}</div>
        )}
      />,
      <DataGridHeader<CandidateReputationDetails>
        key="respondentName"
        name="respondentName"
        width={216}
        pinned="left"
        displayName="Respondent Name"
        sortingOrder={['asc', 'desc', null]}
        cellRender={({ data }) => (
          <div>{data.respondentName || EMPTY_VALUE}</div>
        )}
      />,
    ];

    if (!candidateId) {
      columns.push(
        <DataGridHeader<CandidateReputationDetails>
          key="candidateName"
          name="candidateName"
          width={304}
          displayName="Talent Name"
          sortingOrder={['asc', 'desc', null]}
          cellRender={({ data }) => (
            <div>{data.candidate.calculatedDisplayName || EMPTY_VALUE}</div>
          )}
        />
      );
    }

    columns.push(
      <DataGridHeader<CandidateReputationDetails>
        key="meetsExpectations"
        name="meetsExpectations"
        width={200}
        displayName="Met Expectations"
        sortingOrder={['asc', 'desc', null]}
        cellRender={({ data }) => {
          if (data.meetsExpectations === null) return <div>{EMPTY_VALUE}</div>;
          return (
            <Flashy
              color={data.meetsExpectations ? 'dataVizGreen' : 'dataVizRed'}
            >
              {data.meetsExpectations ? 'YES' : 'NO'}
            </Flashy>
          );
        }}
      />,
      <DataGridHeader<CandidateReputationDetails>
        key="clientRecommendScore"
        name="clientRecommendScore"
        width={152}
        displayName="Talent NPS"
        cellRender={({ data }) => (
          <Flashy color="dataVizGreen">
            {data.clientRecommendScore == null
              ? EMPTY_VALUE
              : `${data.clientRecommendScore}/10`}
          </Flashy>
        )}
      />,
      ...scoreColumns.map(({ key, width, displayName }) => (
        <DataGridHeader<CandidateReputationDetails>
          key={key}
          name={key}
          width={width}
          displayName={displayName}
          cellRender={scoreOutOf5CellRender({ field: key })}
        />
      )),
      <DataGridHeader<CandidateReputationDetails>
        key="inTopTenPercent"
        name="inTopTenPercent"
        width={144}
        displayName="Top 10%?"
        sortingOrder={['asc', 'desc', null]}
        cellRender={({ data }) => {
          if (data.inTopTenPercent === null) return <div>{EMPTY_VALUE}</div>;
          return (
            <Flashy
              color={data.inTopTenPercent ? 'dataVizGreen' : 'dataVizRed'}
            >
              {data.inTopTenPercent ? 'YES' : 'NO'}
            </Flashy>
          );
        }}
      />,
      <DataGridHeader<CandidateReputationDetails>
        key="didWellOn_retiredDidWellOnImproveOn"
        name="didWellOn_retiredDidWellOnImproveOn"
        width={320}
        displayName="What did talent do well?"
        tooltip
        valueGetter={({ data }) =>
          data?.didWellOn ?? data?.retiredDidWellOnImproveOn ?? EMPTY_VALUE
        }
      />,
      <DataGridHeader<CandidateReputationDetails>
        key="improveOn"
        name="improveOn"
        width={320}
        displayName="What can talent improve?"
        tooltip
        valueGetter={({ data }) => data?.improveOn ?? EMPTY_VALUE}
      />,
      <DataGridHeader<CandidateReputationDetails>
        key="axiomClientRecommendScore"
        name="axiomClientRecommendScore"
        displayName="Net Promoter Score"
        cellRender={({ data }) => (
          <div>{data.axiomClientRecommendScore ?? EMPTY_VALUE}</div>
        )}
      />,
      <DataGridHeader<CandidateReputationDetails>
        key="axiomDidWellOn"
        name="axiomDidWellOn"
        width={320}
        displayName="What is Axiom doing well?"
        tooltip
        valueGetter={({ data }) => data?.axiomDidWellOn ?? EMPTY_VALUE}
      />,
      <DataGridHeader<CandidateReputationDetails>
        key="axiomImproveOn"
        name="axiomImproveOn"
        width={320}
        displayName="What can Axiom do better?"
        tooltip
        valueGetter={({ data }) => data?.axiomImproveOn ?? EMPTY_VALUE}
      />,
      <DataGridHeader<CandidateReputationDetails>
        key="engagementName"
        name="engagementName"
        width={224}
        displayName="Engagement Name"
        sortingOrder={['asc', 'desc', null]}
        cellRender={({ data }) => (
          <div>{data.opportunity.jobName || EMPTY_VALUE}</div>
        )}
      />,
      <DataGridHeader<CandidateReputationDetails>
        key="accountOwner"
        name="accountOwner"
        width={160}
        displayName="Account Owner"
        sortingOrder={['asc', 'desc', null]}
        cellRender={({ data }) => {
          return (
            <div>
              {data.opportunity.account?.ownerUser?.fullName ?? EMPTY_VALUE}
            </div>
          );
        }}
      />,
      <DataGridHeader<CandidateReputationDetails>
        key="matterId"
        name="matterId"
        width={160}
        displayName="Matter ID"
        sortingOrder={['asc', 'desc', null]}
        cellRender={({ data }) => <div>{data.matterId || EMPTY_VALUE}</div>}
      />,
      <DataGridHeader<CandidateReputationDetails>
        key="history"
        name="history"
        width={120}
        displayName="History"
        cellRender={({ data }) => (
          <Layout position="center middle">
            <IconButton
              name="OPEN_HISTORY"
              icon="open"
              variation="minimal"
              pattern="secondary"
              onClick={async () => {
                await CandidateReputationDetailsApi.invalidateReputationHistory(
                  { reputationId: data.id }
                );
                setHistoryData(data);
              }}
            />
          </Layout>
        )}
      />,
      <DataGridHeader<CandidateReputationDetails>
        key="cta"
        name="cta"
        width={64}
        displayName=""
        pinned="right"
        lockPinned
        cellRender={props => {
          const { node, column, value, api } = props;

          return (
            <IconButton
              name={`CONTEXT_MENU_${node.data?.id ?? 'unknown'}`}
              icon="vertical-ellipsis"
              variation="minimal"
              pattern="secondary"
              onClick={event => {
                api.showContextMenu({
                  rowNode: node,
                  column,
                  value,
                  x: event.clientX,
                  y: event.clientY,
                });
              }}
            />
          );
        }}
      />
    );

    return columns;
  },
  getContextMenuItems: ({
    params,
    user,
    onClickSendFeedBack,
    onClickSentFeedBack,
    onClickDispute,
    onClickDismiss,
  }: {
    params: GetContextMenuItemsParams<CandidateReputationDetails>;
    user: User;
    onClickSendFeedBack: (arg: CandidateReputationDetails) => void;
    onClickSentFeedBack: (arg: CandidateReputationDetails) => Promise<void>;
    onClickDispute: (arg: CandidateReputationDetails) => void;
    onClickDismiss: (arg: CandidateReputationDetails) => void;
  }): ContextMenuItem[] => {
    const { column, node } = params;
    const menuItems: ContextMenuItem[] = [];

    const canAction = isEnvoyAdmin(user) || isEnvoyUser(user);

    if (!node?.data || column?.getColId() !== 'cta') {
      return menuItems;
    }

    const { data } = node;
    const sendFeedback = createMenuItem({
      name: 'Email feedback to talent',
      testId: 'SEND_FEEDBACK',
      action: () => {
        onClickSendFeedBack(data);
      },
    });
    const sentFeedback = createMenuItem({
      name: 'Mark as shared with talent',
      testId: 'SENT_FEEDBACK',
      action: () => {
        void onClickSentFeedBack(data);
      },
    });
    const disputeFeedback = createMenuItem({
      name: 'Dispute feedback',
      testId: 'DISPUTE_FEEDBACK',
      action: () => {
        onClickDispute(data);
      },
    });
    const dismissFeedback = createMenuItem({
      name: 'Dismiss without sending',
      testId: 'DISMISS_WITHOUT_SENDING',
      action: () => {
        onClickDismiss(data);
      },
    });

    if (canAction) {
      menuItems.push(sendFeedback, sentFeedback);

      if (DateUtil.getDaysSinceNow(data.responseDate) <= 90) {
        menuItems.push(disputeFeedback);
      }

      if (!data.historical) {
        menuItems.push(dismissFeedback);
      }
    }

    return menuItems;
  },

  runDelay: async (): Promise<void> => {
    return new Promise<void>(resolve => {
      setTimeout(resolve, 1500);
    });
  },
};
